// components/dashboard/widgets/ProjetosPendentes.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty, List, Tag, Avatar, Tooltip, Badge } from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useDashboardData } from '../../../hooks/useDashboardData';

const ProjetosPendentes = ({ endpoint, filtros, limite }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Carregando projetos pendentes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: '6px',
        color: '#a8071a'
      }}>
        <ExclamationCircleOutlined style={{ fontSize: 24, marginBottom: 8 }} />
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar projetos pendentes
        </p>
        <small style={{ color: '#cf1322' }}>{error?.message || String(error)}</small>
      </div>
    );
  }

  if (!dados || dados.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: 40,
        color: '#52c41a'
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
        <h4 style={{ color: '#52c41a', margin: 0 }}>
          Nenhum projeto pendente!
        </h4>
        <p style={{ color: '#8c8c8c', margin: '8px 0 0 0' }}>
          Todos os projetos estão em andamento.
        </p>
      </div>
    );
  }

  const getPrioridade = (diasSemSubmissao, totalDefesas) => {
    if (diasSemSubmissao == null) {
      return totalDefesas > 0 ? 'medio' : 'baixo';
    }
    if (diasSemSubmissao > 60) return 'critico';
    if (diasSemSubmissao > 30) return 'alto';
    if (diasSemSubmissao > 14) return 'medio';
    return 'baixo';
  };

  const getStatusVisual = (dataSubmissao, totalDefesas) => {
    if (!dataSubmissao) {
      return {
        texto: 'Não Submetido',
        cor: '#f5222d',
        icone: <ExclamationCircleOutlined />
      };
    }
    if (totalDefesas === 0) {
      return {
        texto: 'Aguardando Defesa',
        cor: '#fa8c16',
        icone: <ClockCircleOutlined />
      };
    }
    return {
      texto: 'Em Andamento',
      cor: '#1890ff',
      icone: <FileTextOutlined />
    };
  };

  const dadosProcessados = dados.map(item => {
    const dataSubmissao = item.data_submissao ? new Date(item.data_submissao) : null;
    const agora = new Date();
    const diasSemSubmissao = dataSubmissao
      ? Math.floor((agora - dataSubmissao) / (1000 * 60 * 60 * 24))
      : null;

    return {
      projeto_id: item.projeto_id,
      titulo_proposta: item.titulo_proposta,
      estudante: item.estudante,
      data_submissao: item.data_submissao,
      total_defesas: item.total_defesas,
      data_submissao_obj: dataSubmissao,
      dias_sem_submissao: diasSemSubmissao,
      prioridade: getPrioridade(diasSemSubmissao, item.total_defesas),
      status_visual: getStatusVisual(item.data_submissao, item.total_defesas)
    };
  });

  const prioridadeOrder = { critico: 0, alto: 1, medio: 2, baixo: 3 };
  const dadosOrdenados = dadosProcessados.sort((a, b) => {
    if (prioridadeOrder[a.prioridade] !== prioridadeOrder[b.prioridade]) {
      return prioridadeOrder[a.prioridade] - prioridadeOrder[b.prioridade];
    }
    if (a.data_submissao_obj && b.data_submissao_obj) {
      return a.data_submissao_obj - b.data_submissao_obj;
    }
    return a.data_submissao_obj ? 1 : -1;
  });

  const estatisticas = {
    total: dadosOrdenados.length,
    naoSubmetidos: dadosOrdenados.filter(item => !item.data_submissao).length,
    criticos: dadosOrdenados.filter(item => item.prioridade === 'critico').length,
    comDefesas: dadosOrdenados.filter(item => item.total_defesas > 0).length
  };

  const formatarData = (data) => {
    if (!data) return 'Não submetido';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatarDiasAtras = (dias) => {
    if (dias == null) return '';
    if (dias === 0) return 'hoje';
    if (dias === 1) return 'ontem';
    return `${dias} dias atrás`;
  };

  const getCorPrioridade = (prioridade) => {
    const cores = {
      critico: '#f5222d',
      alto: '#fa8c16',
      medio: '#fadb14',
      baixo: '#52c41a'
    };
    return cores[prioridade] || '#8c8c8c';
  };

  return (
    <div style={{ height: 350, overflowY: 'auto' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 8,
        marginBottom: 16,
        padding: '8px',
        backgroundColor: '#fafafa',
        borderRadius: '4px',
        fontSize: 11
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{estatisticas.total}</div>
          <div style={{ color: '#666' }}>Total</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#f5222d' }}>{estatisticas.naoSubmetidos}</div>
          <div style={{ color: '#666' }}>Não Submetidos</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>{estatisticas.criticos}</div>
          <div style={{ color: '#666' }}>Críticos</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#52c41a' }}>{estatisticas.comDefesas}</div>
          <div style={{ color: '#666' }}>Com Defesas</div>
        </div>
      </div>

      <List
        dataSource={dadosOrdenados.slice(0, limite)}
        renderItem={(item) => (
          <List.Item style={{
            padding: '12px',
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: item.prioridade === 'critico' ? '#fff2f0' : 'transparent'
          }}>
            <List.Item.Meta
              avatar={
                <Badge
                  count={item.prioridade === 'critico' ? '!' : null}
                  style={{ backgroundColor: '#f5222d' }}
                >
                  <Avatar
                    icon={<UserOutlined />}
                    style={{
                      backgroundColor: getCorPrioridade(item.prioridade)
                    }}
                  />
                </Badge>
              }
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 'bold', fontSize: 13, color: '#262626' }}>
                    {item.estudante || 'Estudante Anônimo'}
                  </span>
                  <Tag
                    color={getCorPrioridade(item.prioridade)}
                    style={{ margin: 0, fontSize: 10 }}
                  >
                    {item.prioridade.toUpperCase()}
                  </Tag>
                  <Tag
                    color={item.status_visual.cor}
                    icon={item.status_visual.icone}
                    style={{ margin: 0, fontSize: 10 }}
                  >
                    {item.status_visual.texto}
                  </Tag>
                </div>
              }
              description={
                <div style={{ fontSize: 12 }}>
                  <div style={{
                    fontWeight: 'bold',
                    color: '#1890ff',
                    marginBottom: 4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    📋 {item.titulo_proposta || 'Título não informado'}
                  </div>
                  <div style={{ color: '#666', marginBottom: 4 }}>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    Submetido: {formatarData(item.data_submissao)}
                    {item.dias_sem_submissao != null && (
                      <span style={{
                        marginLeft: 8,
                        color: item.prioridade === 'critico' ? '#f5222d' : '#fa8c16'
                      }}>
                        ({formatarDiasAtras(item.dias_sem_submissao)})
                      </span>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: 12,
                    fontSize: 11,
                    color: '#8c8c8c',
                    flexWrap: 'wrap'
                  }}>
                    <span>ID: {item.projeto_id}</span>
                    {item.total_defesas > 0 && (
                      <span>Defesas: {item.total_defesas}</span>
                    )}
                  </div>
                </div>
              }
            />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end', gap: 4 }}>
              <Tooltip title="Ver detalhes do projeto">
                <span style={{
                  color: '#1890ff',
                  cursor: 'pointer',
                  fontSize: 11,
                  textDecoration: 'underline'
                }}>
                  Ver detalhes
                </span>
              </Tooltip>
              {!item.data_submissao && (
                <Tooltip title="Projeto ainda não foi submetido">
                  <Tag color="red" style={{ fontSize: 10, margin: 0 }}>
                    URGENTE
                  </Tag>
                </Tooltip>
              )}
            </div>
          </List.Item>
        )}
      />

      {dadosOrdenados.length > limite && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fafafa',
          borderTop: '1px solid #f0f0f0',
          textAlign: 'center',
          fontSize: 12,
          color: '#666'
        }}>
          Mostrando {limite} de <strong>{dadosOrdenados.length}</strong> projetos pendentes
        </div>
      )}

      {dadosOrdenados.length > 0 && (
        <div style={{
          marginTop: 8,
          fontSize: 11,
          color: '#666',
          textAlign: 'center'
        }}>
          <strong>Legenda:</strong>{' '}
          <span style={{ color: '#f5222d' }}>Crítico (&gt;60 dias)</span> •
          <span style={{ color: '#fa8c16' }}> Alto (30-60 dias)</span> •
          <span style={{ color: '#fadb14' }}> Médio (14-30 dias)</span> •
          <span style={{ color: '#52c41a' }}> Baixo (&lt;14 dias)</span>
        </div>
      )}
    </div>
  );
};

ProjetosPendentes.displayName = 'ProjetosPendentes';
ProjetosPendentes.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object,
  limite: PropTypes.number
};

ProjetosPendentes.defaultProps = {
  filtros: {},
  limite: 20
};

export default ProjetosPendentes;
